/**
 * Room governance — in-room session matches (`matches/{roomId}/roomMatches/{matchId}`).
 * Canonical completed games remain in `matchRecords/` (ADR-019).
 */

/** @typedef {'draft' | 'ready' | 'live' | 'completed' | 'cancelled'} RoomMatchStatus */

/** @typedef {'owner' | 'deputy' | 'player'} RoomGovernanceRole */

/** @typedef {'available' | 'playing' | 'offline'} RoomPlayerAvailability */

/**
 * @typedef {Object} RoomSessionMatch
 * @property {string} id UUID
 * @property {string} roomId Normalized room code (parent doc id)
 * @property {string | null} competitionId
 * @property {string | null} [fixtureId] Managed-draw fixture when launched from Tournament Experience
 * @property {string | null} [drawId]
 * @property {string | null} [entryAId]
 * @property {string | null} [entryBId]
 * @property {number | null} courtNumber
 * @property {RoomMatchStatus} status
 * @property {string} createdBy Auth uid
 * @property {string} managedBy Auth uid — single editor at a time
 * @property {string | null} lockedBy Auth uid holding manage lock
 * @property {import('firebase/firestore').Timestamp | null} lockedAt
 * @property {string[]} participantEntryIds Registry entry ids assigned to this match
 * @property {number} sequenceNumber 1-based match number in room (UI)
 * @property {import('firebase/firestore').Timestamp} createdAt
 * @property {import('firebase/firestore').Timestamp} updatedAt
 */

export const ROOM_MATCH_STATUSES = [
  "draft",
  "ready",
  "live",
  "completed",
  "cancelled",
];

/** Stale manage-lock threshold (ms); another referee may take over after this. */
export const ROOM_MATCH_LOCK_STALE_MS = 15 * 60 * 1000;

/** Casual Arena: release ready-court / stale ready roomMatches after this idle period (no live scoring). */
export const CASUAL_COURT_IDLE_RELEASE_MS = 10 * 60 * 1000;

export function newRoomMatchId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `rm_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}
